import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    Avatar,
    Chip,
    Stepper,
    Typography,
    IconButton,
    Grid,
    Step,
    StepLabel,
    Box,
    Button,
    Paper,
    FormControl,
    TextareaAutosize,
} from "@mui/material/";
import SignatureCanvas from "./SignatureCanvas";
import DealerDetails from "./DealerDetails";
import {
    useAuditProgressDetails,
    useGroupAuditProgressDetails,
} from "../../common/hooks";
import { useParams } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import actions from "../../Store/actions";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExcludedAssetList from "./ExcludedAssetList";
import styles from "./styles";
import {
    FolderOutlined as FolderOutlinedIcon,
    FolderSpecialOutlined as FolderSpecialOutlinedIcon,
} from "@mui/icons-material";
import AuditProgress from "../GroupedAudits/AuditProgress";
/*
Stepper  Component for Signature
1. Display audit summary
2. Auditor signature
3. Dealer details 
4. Dealer signature 
 */

const SignatureSteps = ({ setCaptureSignatures }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { auditId } = useParams();

    const { seen, excluded, excludedAssetList, alt, assigned, required } =
        useAuditProgressDetails("db", auditId);

    const steps = [
        t("summary"),
        t("dealerContactDetails"),
        t("auditorDetails"),
    ];

    const { auditUpload, auth, audits, geoLocation } = useSelector(
        (state) => state
    );
    const isGroupAudit = audits?.item?.auditReference?.toLowerCase() !== "none";
    const { auditSignature } = auditUpload;
    const {
        groupedSeenAsset,
        groupedExcludedAsset,
        groupedUnexpectedAsset,
        groupedAssignedAsset,
        groupedRequired,
    } = useGroupAuditProgressDetails("db", auditId);

    const currentAuditSignature = auditUpload[auditId]
        ? auditUpload[auditId].auditSignature
        : auditSignature;
    const [validateDealer, setValidateDealer] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [dealerSignature, setDealerSignature] = useState(null);
    const [auditorSignature, setAuditorSignature] = useState(null);
    const [auditorComment, setAuditorComment] = useState(
        currentAuditSignature.auditorComment
    );
    const [openExlcusionReasonList, setOpenExclusionReasonList] =
        useState(false);

    const auditorCommentChange = async (event) => {
        const loadPrevValue = auditUpload[auditId]
            ? {
                  ...currentAuditSignature,
                  ...auditUpload[auditId].auditSignature,
              }
            : currentAuditSignature;

        setAuditorComment(event.target.value);

        await dispatch(
            actions.auditUpload.create.update({
                [auditId]: {
                    auditSignature: {
                        ...loadPrevValue,
                        auditorComment: event.target.value,
                    },
                },
            })
        );
    };

    const handleOpenExcludedList = () => setOpenExclusionReasonList(true);

    const onHandleAuditorSignatureSave = useCallback(
        async (signImg) => {
            const loadPrevValue = auditUpload[auditId]
                ? {
                      ...currentAuditSignature,
                      ...auditUpload[auditId].auditSignature,
                  }
                : currentAuditSignature;

            setAuditorSignature(signImg);

            await dispatch(
                actions.auditUpload.create.update({
                    [auditId]: {
                        auditSignature: {
                            ...loadPrevValue,
                            auditorSignature: signImg,
                            auditorSignatureDate: new Date().toISOString(),
                            auditorLatitude: geoLocation.coords.latitude,
                            auditorLongitude: geoLocation.coords.longitude,
                        },
                    },
                })
            );
            setCurrentStep(3); // Move to the submit step
            setCaptureSignatures(true);
        },
        [auditUpload]
    );

    const onResign = useCallback(
        async (sign, signDate) => {
            const loadPrevValue = auditUpload[auditId]
                ? {
                      ...currentAuditSignature,
                      ...auditUpload[auditId].auditSignature,
                  }
                : currentAuditSignature;

            await dispatch(
                actions.auditUpload.create.update({
                    [auditId]: {
                        auditSignature: {
                            ...loadPrevValue,
                            [sign]: "",
                            [signDate]: "",
                        },
                    },
                })
            );
        },
        [auditUpload]
    );

    const onHandleDealerSignatureSave = useCallback(
        async (signImg) => {
            const loadPrevValue = auditUpload[auditId]
                ? {
                      ...currentAuditSignature,
                      ...auditUpload[auditId].auditSignature,
                  }
                : currentAuditSignature;

            setDealerSignature(signImg);

            await dispatch(
                actions.auditUpload.create.update({
                    [auditId]: {
                        auditSignature: {
                            ...loadPrevValue,
                            dealerContactSignature: signImg,
                            dealerContactSignatureDate:
                                new Date().toISOString(),
                            dealerLatitude: geoLocation.coords.latitude,
                            dealerLongitude: geoLocation.coords.longitude,
                        },
                    },
                })
            );
            setCurrentStep(2);

            // Move to the next step Summary
            //setCaptureSignatures(true);
        },
        [auditUpload]
    );

    const handleCloseAddUser = useCallback((event, reason) => {
        if (reason && reason === "backdropClick") return;
        setAddUserOpen(false);
    }, []);

    const handleBack = () => {
        if (currentStep === 1) {
            setCurrentStep(0);
        } else if (currentStep === 2) {
            setCurrentStep(1);
        } else if (currentStep === 3) {
            setCurrentStep(2);
        }
    };

    const onExcludedAssetListClose = (event, reason) => {
        if (reason && reason == "backdropClick") return;
        setOpenExclusionReasonList(false);
    };

    const ProgressIndicator = ({ name, value, icon, color, clickFn }) => {
        return (
            value > 0 && (
                <Box key={name} onClick={clickFn || (() => {})}>
                    <Typography
                        component="div"
                        sx={{ textDecoration: clickFn ? "underline" : "none" }}
                    >
                        {t(name)}
                    </Typography>
                    <Chip
                        icon={icon}
                        label={value}
                        color={color}
                        variant="outlined"
                        sx={{ border: "none" }}
                    />
                </Box>
            )
        );
    };

    const createIconTextLink = (
        IconComponent,
        text,
        color,
        clickFn,
        fullWidth,
        alignCenter
    ) => {
        return (
            <Grid
                item
                xs={fullWidth ? 12 : 6}
                sx={{ justifyItems: alignCenter ? "center" : "baseline" }}
            >
                <Button
                    onClick={clickFn || (() => {})}
                    startIcon={<IconComponent color={color} />}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        textTransform: "none",
                        textDecoration: clickFn ? "underline" : "none",
                        color: "inherit",
                        "&:hover .underline": {
                            textDecoration: "underline",
                        },
                    }}
                >
                    <Typography variant="body1" className="underline">
                        {text}
                    </Typography>
                </Button>
            </Grid>
        );
    };

    const generateHeader = (text, isBackRequired) => (
        <Box sx={styles.paperHeader}>
            {isBackRequired ? (
                <IconButton onClick={handleBack}>
                    <ArrowBackIcon />
                </IconButton>
            ) : (
                <Typography />
            )}
            <Typography variant="button" gutterBottom>
                {text}
            </Typography>
            <Typography />
        </Box>
    );

    return (
        <Box>
            <Stepper activeStep={currentStep} alternativeLabel>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{t(label)}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            <Box sx={{ mt: 2 }}>
                {currentStep === 0 && (
                    <Paper elevation={3} sx={{ p: 3 }}>
                        {generateHeader(t("summary"), false)}
                        <Grid
                            container
                            sx={{ borderBottom: "1px solid ", py: 2 }}
                        >
                            {createIconTextLink(
                                FolderOutlinedIcon,
                                `${t("currentLocation")}`,
                                "primary",
                                null,
                                true,
                                true
                            )}
                            {createIconTextLink(
                                CheckIcon,
                                `${assigned} ${t("total")}`,
                                "secondary",
                                null,
                                false
                            )}
                            {createIconTextLink(
                                RotateLeftIcon,
                                `${alt} ${t("unexpected")}`,
                                "warning",
                                null,
                                false
                            )}
                            {createIconTextLink(
                                VisibilityIcon,
                                `${seen} ${t("seen")}`,
                                "primary",
                                null,
                                true
                            )}
                            {createIconTextLink(
                                VisibilityOffIcon,
                                `${excluded} ${t("excluded")}`,
                                "error",
                                handleOpenExcludedList,
                                true
                            )}
                            {createIconTextLink(
                                RotateLeftIcon,
                                `${required} ${t("incomplete")}`,
                                "primary",
                                null,
                                true
                            )}
                        </Grid>
                        {isGroupAudit && (
                            <Grid container>
                                {createIconTextLink(
                                    FolderSpecialOutlinedIcon,
                                    `${t("totalAssets")}`,
                                    "primary",
                                    null,
                                    true,
                                    true
                                )}
                                {createIconTextLink(
                                    CheckIcon,
                                    `${groupedAssignedAsset} ${t("total")}`,
                                    "secondary",
                                    null,
                                    false
                                )}
                                {createIconTextLink(
                                    VisibilityIcon,
                                    `${groupedSeenAsset} ${t("seen")}`,
                                    "primary",
                                    null,
                                    true
                                )}
                                {createIconTextLink(
                                    VisibilityOffIcon,
                                    `${groupedExcludedAsset} ${t("excluded")}`,
                                    "error",
                                    handleOpenExcludedList,
                                    true
                                )}
                                {createIconTextLink(
                                    RotateLeftIcon,
                                    `${groupedRequired} ${t("incomplete")}`,
                                    "primary",
                                    null,
                                    true
                                )}
                            </Grid>
                        )}

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setCurrentStep(1)}
                            sx={{ m: 2 }}
                        >
                            {t("next")}
                        </Button>
                    </Paper>
                )}
                {currentStep === 1 && (
                    <Paper elevation={3} sx={{ p: 3 }}>
                        {generateHeader(t("dealerContactDetails"), true)}
                        <Grid container>
                            <Grid item xs={12} sx={{ pb: 5 }}>
                                <DealerDetails
                                    validateDealer={validateDealer}
                                    setValidateDealer={setValidateDealer}
                                    onCloseAddUser={handleCloseAddUser}
                                />

                                {currentAuditSignature.dealerContactSignature
                                    ?.length > 1 ? (
                                    <>
                                        <Avatar
                                            sx={styles.signatureImage}
                                            alt={
                                                currentAuditSignature.dealerContactName
                                            }
                                            src={
                                                currentAuditSignature.dealerContactSignature
                                            }
                                        />
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => {
                                                onResign(
                                                    "dealerContactSignature",
                                                    "dealerContactSignatureDate"
                                                );
                                                setCurrentStep(1);
                                            }}
                                            sx={{ m: 2 }}
                                        >
                                            {t("resign")}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => setCurrentStep(2)}
                                            sx={{ m: 2 }}
                                        >
                                            {t("next")}
                                        </Button>
                                    </>
                                ) : (
                                    <SignatureCanvas
                                        onSaveDisabled={validateDealer}
                                        label={`${t(
                                            "dealerContactSignature"
                                        )} *`}
                                        onSave={onHandleDealerSignatureSave}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Paper>
                )}
                {currentStep === 2 && (
                    <Paper elevation={3} sx={{ p: 3 }}>
                        {generateHeader(t("dealerContactDetails"), true)}
                        <FormControl
                            fullWidth
                            margin="dense"
                            component="form"
                            sx={styles.textArea}
                        >
                            <TextareaAutosize
                                sx={styles.textArea}
                                minRows={2}
                                maxRows={4}
                                placeholder={t("auditorComment")}
                                value={auditorComment}
                                onChange={auditorCommentChange}
                            />
                        </FormControl>
                        {currentAuditSignature.auditorSignature?.length > 1 ? (
                            <>
                                <Avatar
                                    sx={styles.signatureImage}
                                    alt={auth.account.displayName}
                                    src={currentAuditSignature.auditorSignature}
                                />
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => {
                                        onResign(
                                            "auditorSignature",
                                            "auditorSignatureDate"
                                        );
                                        setCurrentStep(2);
                                    }}
                                    sx={{ m: 2 }}
                                >
                                    {t("resign")}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        setCurrentStep(3);
                                        setCaptureSignatures(true);
                                    }}
                                    sx={{ m: 2 }}
                                >
                                    {t("next")}
                                </Button>
                            </>
                        ) : (
                            <SignatureCanvas
                                label={t("auditorSignature")}
                                onSaveDisabled={true}
                                signedByText={`${t("signedBy")} ${
                                    auth.account.displayName
                                }`}
                                onSave={onHandleAuditorSignatureSave}
                            />
                        )}
                    </Paper>
                )}
            </Box>

            {openExlcusionReasonList && (
                <ExcludedAssetList
                    excludedAssetList={excludedAssetList}
                    onExcludedAssetListClose={onExcludedAssetListClose}
                    openExlcusionReasonList={openExlcusionReasonList}
                />
            )}
        </Box>
    );
};

export default SignatureSteps;
